import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

// Comentários em português; código em inglês
// Este contexto fornece dados de branding (logos, textos e ícones) para toda a aplicação

const BrandingContext = createContext(null);

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ children }) => {
	const [branding, setBranding] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Base da API para chamadas públicas (usa REACT_APP_API_URL em dev)
	const API_BASE = (process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location && `${window.location.origin.replace(/\/$/, '')}/api`) || '').replace(/\/$/, '');
	// Origem base para assets (ex.: http://localhost:3000)
	const ASSET_BASE = API_BASE.replace(/\/api$/, '');

	// Helper para resolver URL de asset: converte caminhos relativos para absolutos na origem da API
	const resolveAssetUrl = (url) => {
		if (!url) return undefined;
		if (/^https?:\/\//i.test(url)) return url;
		// Garante barra inicial
		const path = url.startsWith('/') ? url : `/${url}`;
		return `${ASSET_BASE}${path}`;
	};

	// Normaliza a resposta do backend resolvendo todas as URLs de assets
	const resolveBrandingResponse = (data) => {
		if (!data) return data;
		const a = data.assets || {};
		const resolved = {
			...data,
			assets: {
				...a,
				sidebar: {
					light: resolveAssetUrl(a.sidebar?.light),
					dark: resolveAssetUrl(a.sidebar?.dark),
				},
				sidebarOpen: {
					light: resolveAssetUrl(a.sidebarOpen?.light),
					dark: resolveAssetUrl(a.sidebarOpen?.dark),
				},
				sidebarClosed: {
					light: resolveAssetUrl(a.sidebarClosed?.light),
					dark: resolveAssetUrl(a.sidebarClosed?.dark),
				},
				login: { main: resolveAssetUrl(a.login?.main) },
				panel: { main: resolveAssetUrl(a.panel?.main) },
				reports: {
					header: resolveAssetUrl(a.reports?.header),
					footer: resolveAssetUrl(a.reports?.footer),
				},
				icons: {
					favicon16: resolveAssetUrl(a.icons?.favicon16),
					favicon32: resolveAssetUrl(a.icons?.favicon32),
					appleTouchIcon180: resolveAssetUrl(a.icons?.appleTouchIcon180),
					pwaIcon192: resolveAssetUrl(a.icons?.pwaIcon192),
					pwaIcon512: resolveAssetUrl(a.icons?.pwaIcon512),
					manifestUrl: resolveAssetUrl(a.icons?.manifestUrl) || `${API_BASE}/public/branding/manifest.json`,
				},
			},
		};
		return resolved;
	};

	// Busca o branding público (sem exigir autenticação)
	useEffect(() => {
		let cancelled = false;
		const fetchBranding = async () => {
			setLoading(true);
			setError(null);
			try {
				// Usar um client axios separado para evitar redirecionamento 401
				const response = await fetch(`${API_BASE}/public/branding`);
				if (!response.ok) throw new Error('Falha ao carregar branding');
				const json = await response.json();
				if (!cancelled) {
					setBranding(resolveBrandingResponse(json.data));
				}
			} catch (e) {
				if (!cancelled) setError(e.message || 'Erro ao carregar branding');
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		fetchBranding();
		return () => { cancelled = true; };
	}, []);

	// Injetar ícones/manifest dinamicamente quando branding estiver disponível
	useEffect(() => {
		if (!branding) return;
		const head = document.head;
		const links = [];

		// Helper para criar/atualizar link
		const upsertLink = (rel, href, sizes) => {
			if (!href) return;
			const link = document.createElement('link');
			link.rel = rel;
			if (sizes) link.sizes = sizes;
			link.href = href + `?v=${branding.version}`;
			head.appendChild(link);
			links.push(link);
		};

		const icons = branding.assets?.icons || {};
		upsertLink('icon', icons.favicon16, '16x16');
		upsertLink('icon', icons.favicon32, '32x32');
		upsertLink('apple-touch-icon', icons.appleTouchIcon180, '180x180');
		// Manifest dinâmico (garante URL absoluta)
		if (icons.manifestUrl) {
			const manifestHref = icons.manifestUrl.startsWith('http') ? icons.manifestUrl : `${API_BASE}/public/branding/manifest.json`;
			upsertLink('manifest', manifestHref);
		}

		return () => {
			links.forEach(l => {
				try { head.removeChild(l); } catch {}
			});
		};
	}, [branding]);

	// Helper para obter logo por slot/variant
	const getLogo = useMemo(() => {
		return (slot, variant = 'light') => {
			const assets = branding?.assets || {};
			switch (slot) {
				case 'sidebar':
					return variant === 'dark' ? assets.sidebar?.dark : assets.sidebar?.light;
				case 'sidebar-open':
					return variant === 'dark' ? assets.sidebarOpen?.dark : assets.sidebarOpen?.light;
				case 'sidebar-closed':
					return variant === 'dark' ? assets.sidebarClosed?.dark : assets.sidebarClosed?.light;
				case 'login':
					return assets.login?.main;
				case 'panel':
					return assets.panel?.main;
				case 'reports-header':
					return assets.reports?.header;
				case 'reports-footer':
					return assets.reports?.footer;
				default:
					return undefined;
			}
		};
	}, [branding]);

	// Helper para obter ícones
	const getIcon = useMemo(() => {
		return (name) => {
			const icons = branding?.assets?.icons || {};
			switch (name) {
				case 'favicon16': return icons.favicon16;
				case 'favicon32': return icons.favicon32;
				case 'apple180': return icons.appleTouchIcon180;
				case 'pwa192': return icons.pwaIcon192;
				case 'pwa512': return icons.pwaIcon512;
				default: return undefined;
			}
		};
	}, [branding]);

	const value = useMemo(() => ({
		branding,
		loading,
		error,
		getLogo,
		getIcon,
		name: branding?.displayName,
		subtitle: branding?.displaySubtitle,
		version: branding?.version || '1',
		sizing: branding?.sizing || {},
		textSizing: branding?.textSizing || {},
		textVisibility: branding?.textVisibility || {},
		// Helpers de visibilidade por contexto, com fallback global
		isTitleVisible: (ctx) => {
			const v = branding?.textVisibility || {};
			const perCtx = v?.[ctx]?.showTitle;
			if (typeof perCtx === 'boolean') return perCtx;
			if (typeof v.showTitle === 'boolean') return v.showTitle;
			return true;
		},
		isSubtitleVisible: (ctx) => {
			const v = branding?.textVisibility || {};
			const perCtx = v?.[ctx]?.showSubtitle;
			if (typeof perCtx === 'boolean') return perCtx;
			if (typeof v.showSubtitle === 'boolean') return v.showSubtitle;
			return true;
		},
	}), [branding, loading, error, getLogo, getIcon]);

	return (
		<BrandingContext.Provider value={value}>
			{children}
		</BrandingContext.Provider>
	);
};
